import { useEffect, useState, type ReactNode } from "react";
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import {
  DOMAINS,
  OPEN_MAX_LENGTH,
  OPEN_QUESTIONS,
  SCALES,
  SHIR_DERECH_YEAR,
  type QuestionnaireKind,
} from "@/lib/shirDerech";

// Public questionnaire page. Rendered OUTSIDE the WorkOS auth gate (see
// main.tsx), so it must never call useApi() or read any dashboard data.
// The only network call is POST /api/shir-derech/submit.

type Draft = {
  fullName: string;
  scores: Record<string, number>;
  answers: Record<string, string>;
};

const EMPTY: Draft = { fullName: "", scores: {}, answers: {} };

function draftKey(kind: QuestionnaireKind) {
  return `shir-derech-draft:${SHIR_DERECH_YEAR}:${kind}`;
}

function loadDraft(kind: QuestionnaireKind): Draft {
  try {
    const raw = localStorage.getItem(draftKey(kind));
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<Draft>;
    return {
      fullName: typeof parsed.fullName === "string" ? parsed.fullName : "",
      scores: parsed.scores && typeof parsed.scores === "object" ? parsed.scores : {},
      answers: parsed.answers && typeof parsed.answers === "object" ? parsed.answers : {},
    };
  } catch {
    return EMPTY;
  }
}

function saveDraft(kind: QuestionnaireKind, d: Draft) {
  try {
    localStorage.setItem(draftKey(kind), JSON.stringify(d));
  } catch {
    // storage unavailable: the form still works, the draft just isn't kept
  }
}

function clearDraft(kind: QuestionnaireKind) {
  try {
    localStorage.removeItem(draftKey(kind));
  } catch {
    // ignore
  }
}

const TITLES: Record<QuestionnaireKind, string> = {
  opening: "שאלון פתיחה · שיר דרך",
  closing: "שאלון סיום · שיר דרך",
};

export function PublicQuestionnaire({ kind }: { kind: QuestionnaireKind }) {
  const openQuestions = OPEN_QUESTIONS[kind];
  const totalSteps = DOMAINS.length + 1; // domains + open questions
  const [step, setStep] = useState(0); // 0 = intro, 1..5 = domains, 6 = open
  const [draft, setDraft] = useState<Draft>(() => loadDraft(kind));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot

  useEffect(() => {
    document.title = `${TITLES[kind]} ${SHIR_DERECH_YEAR}`;
    document.documentElement.dir = "rtl";
    document.documentElement.lang = "he";
  }, [kind]);

  useEffect(() => {
    if (!done) saveDraft(kind, draft);
  }, [kind, draft, done]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, done]);

  const domain = step >= 1 && step <= DOMAINS.length ? DOMAINS[step - 1] : null;
  const isOpenStep = step === DOMAINS.length + 1;

  const canContinue =
    step === 0
      ? draft.fullName.trim().length > 0
      : domain
        ? domain.questions.every((q) => typeof draft.scores[String(q.id)] === "number")
        : true;

  function setScore(id: number, value: number) {
    setDraft((d) => ({ ...d, scores: { ...d.scores, [String(id)]: value } }));
  }

  function setAnswer(id: number, value: string) {
    setDraft((d) => ({ ...d, answers: { ...d.answers, [String(id)]: value } }));
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/shir-derech/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind,
          full_name: draft.fullName.trim(),
          scores: draft.scores,
          answers: Object.fromEntries(
            openQuestions.map((q) => [String(q.id), (draft.answers[String(q.id)] ?? "").trim()]),
          ),
          website,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      clearDraft(kind);
      setDone(true);
    } catch {
      setError("אירעה שגיאה בשליחת השאלון. אנא נסה שוב.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Shell>
        <div className="rounded-3xl bg-white p-8 sm:p-12 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
          <h1 className="mt-5 text-2xl sm:text-3xl font-bold text-stone-900">תודה רבה!</h1>
          <p className="mt-2 text-lg text-stone-700">השאלון נשלח בהצלחה</p>
          <p className="mt-6 text-stone-600 leading-relaxed">
            תודה על השתתפותך בתכנית "שיר דרך". התשובות שלך חשובות מאוד ויעזרו לנו להמשיך לפתח
            ולשפר את התכנית.
          </p>
          {kind === "closing" && (
            <p className="mt-3 text-stone-600">אנו מאחלים לך המשך דרך של צמיחה, החלמה ושלווה.</p>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6 text-center">
        <span className="inline-block rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-600 shadow-sm">
          שלב {step} מתוך {totalSteps}
        </span>
        <h1 className="mt-4 text-2xl sm:text-3xl font-bold text-stone-900">{TITLES[kind]}</h1>
        <p className="mt-1 text-sm text-stone-500">שנת {SHIR_DERECH_YEAR}</p>
        <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-white">
          <div
            className="h-full rounded-full bg-violet-500 transition-all"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <div className="rounded-3xl bg-white p-5 sm:p-8 shadow-sm">
        {step === 0 && (
          <Intro kind={kind} fullName={draft.fullName} onName={(v) => setDraft((d) => ({ ...d, fullName: v }))} />
        )}

        {domain && (
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-stone-900">{domain.title}</h2>
            <p className="mt-1 text-sm text-stone-600">{domain.subtitle}</p>
            <div className="mt-6 space-y-7">
              {domain.questions.map((q) => (
                <RatingQuestion
                  key={q.id}
                  number={q.id}
                  text={q.text}
                  labels={SCALES[domain.scale]}
                  value={draft.scores[String(q.id)]}
                  onChange={(v) => setScore(q.id, v)}
                />
              ))}
            </div>
          </div>
        )}

        {isOpenStep && (
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-stone-900">שאלות פתוחות</h2>
            <p className="mt-1 text-sm text-stone-600">אפשר לכתוב בחופשיות. אין תשובות נכונות או לא נכונות.</p>
            <div className="mt-6 space-y-7">
              {openQuestions.map((q) => {
                const v = draft.answers[String(q.id)] ?? "";
                return (
                  <div key={q.id}>
                    <label className="block font-medium text-stone-900 leading-snug">{q.text}</label>
                    <textarea
                      value={v}
                      maxLength={OPEN_MAX_LENGTH}
                      onChange={(e) => setAnswer(q.id, e.target.value)}
                      rows={4}
                      placeholder="הקלד את תשובתך כאן..."
                      className="mt-2 w-full rounded-xl border border-stone-200 bg-stone-50/60 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-violet-300"
                    />
                    <p className="mt-1 text-xs text-stone-400">
                      {OPEN_MAX_LENGTH - v.length} תווים נותרו
                    </p>
                  </div>
                );
              })}
            </div>
            {/* honeypot: hidden from people, bots fill it */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
            />
          </div>
        )}

        {error && <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || submitting}
            className="inline-flex items-center gap-1 rounded-xl px-4 py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
            הקודם
          </button>
          {isOpenStep ? (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "שולח…" : "שליחת השאלון"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canContinue}
              className="inline-flex items-center gap-1 rounded-xl bg-stone-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-stone-700 disabled:opacity-40"
            >
              הבא
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>
        {!canContinue && step > 0 && (
          <p className="mt-3 text-center text-xs text-stone-400">יש לענות על כל השאלות בעמוד כדי להמשיך</p>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div dir="rtl" className="min-h-svh bg-[#F2F7FD] px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-2xl">{children}</div>
    </div>
  );
}

function Intro({
  kind,
  fullName,
  onName,
}: {
  kind: QuestionnaireKind;
  fullName: string;
  onName: (v: string) => void;
}) {
  const openCount = OPEN_QUESTIONS[kind].length;
  return (
    <div className="text-stone-700 leading-relaxed">
      <h2 className="text-lg font-bold text-stone-900">ברוכים הבאים</h2>
      {kind === "opening" ? (
        <>
          <p className="mt-3">שלום וברוכים הבאים לתכנית "שיר דרך"!</p>
          <p className="mt-3">
            שאלון זה נעשה כחלק מתהליך מחקרי להערכת תרומת התכנית, ומטרתו לקבל תמונת מצב לפני תחילת
            התכנית. חשוב שכל משתתפי התכנית ימלאו את השאלון במהלך השבועיים הראשונים להשתתפותם. בסיום
            התכנית תתבקש למלא שאלון דומה, לצורך השוואה ומדידת השינוי.
          </p>
        </>
      ) : (
        <>
          <p className="mt-3">שלום וברוכים הבאים לשאלון הסיום של "שיר דרך"!</p>
          <p className="mt-3">
            שאלון זה נועד לעזור לנו להבין כיצד השתנה מצבך במהלך התכנית והאם התכנית עזרה לך באופן
            משמעותי.
          </p>
        </>
      )}
      <p className="mt-3">תודה על שיתוף הפעולה.</p>
      <p className="mt-2 text-sm text-stone-500">השאלון מנוסח בלשון זכר אך פונה לשני המינים.</p>

      <div className="mt-5 rounded-2xl bg-stone-50 p-4 text-sm">
        <p className="font-semibold text-stone-900">השאלון כולל שני חלקים:</p>
        <ul className="mt-2 list-disc space-y-1 pe-1 ps-5">
          <li>
            <b>חלק א':</b> 25 שאלות כמותיות (בחירה בסקאלה 1 עד 5)
          </li>
          <li>
            <b>חלק ב':</b> {openCount} שאלות פתוחות
          </li>
        </ul>
      </div>
      <div className="mt-3 rounded-2xl bg-violet-50 p-4 text-sm">
        <p>
          <b>זמן מילוי משוער:</b> 10 עד 15 דקות
        </p>
        <p className="mt-1">
          <b>פרטיות:</b> כל המידע שתמסור חסוי ונשמר אצל צוות "שיר דרך" בלבד.
        </p>
      </div>

      <label className="mt-6 block font-semibold text-stone-900">שם מלא</label>
      <input
        type="text"
        value={fullName}
        maxLength={100}
        onChange={(e) => onName(e.target.value)}
        className="mt-2 w-full rounded-xl border border-stone-200 bg-stone-50/60 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-violet-300"
        placeholder="שם פרטי ושם משפחה"
        autoComplete="name"
      />
    </div>
  );
}

function RatingQuestion({
  number,
  text,
  labels,
  value,
  onChange,
}: {
  number: number;
  text: string;
  labels: string[];
  value: number | undefined;
  onChange: (v: number) => void;
}) {
  return (
    <fieldset>
      <legend className="font-medium text-stone-900 leading-snug">
        <span className="text-stone-400">{number}. </span>
        {text}
      </legend>
      <div className="mt-3 grid grid-cols-5 gap-1.5 sm:gap-2">
        {labels.map((label, i) => {
          const v = i + 1;
          const selected = value === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v)}
              aria-pressed={selected}
              className={`flex flex-col items-center justify-start gap-1 rounded-xl border px-1 py-2.5 text-center transition-colors ${
                selected
                  ? "border-violet-500 bg-violet-500 text-white"
                  : "border-stone-200 bg-white text-stone-700 hover:border-violet-300 hover:bg-violet-50"
              }`}
            >
              <span className="text-base font-bold">{v}</span>
              <span className={`text-[10px] sm:text-xs leading-tight ${selected ? "text-white" : "text-stone-500"}`}>
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
